import {
  ChangeDetectionStrategy, Component, DestroyRef, effect, inject, OnInit, OnDestroy, computed, signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, filter } from 'rxjs';
import { SessionService } from '../../core/session/session.service';
import { EntitySelectorComponent } from '../../shared/entity-selector/entity-selector.component';
import { HlmIconComponent, HlmProgressComponent, HlmBadgeComponent, matIcon } from '../../ui';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import { customerStatusLabel, customerStatusTone } from '../../shared/utils/customer-status.util';
import { buildCallViewerLink } from '../../shared/utils/call-viewer-link.util';

interface NavItem {
  label: string;
  route: string;
  icon: string;       // lucide icon name
  disabled: boolean;
}

@Component({
  selector: 'cmt-customer-detail',
  standalone: true,
  imports: [
    RouterOutlet, RouterLink, RouterLinkActive,
    HlmIconComponent, HlmProgressComponent, HlmBadgeComponent,
    EntitySelectorComponent, ErrorStateComponent,
  ],
  templateUrl: './customer-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class CustomerDetailComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  readonly session = inject(SessionService);
  private readonly destroyRef = inject(DestroyRef);

  private routerSub?: Subscription;
  private entitySyncDone = false;

  readonly sidebarExpanded = signal(true);
  readonly activeTabRoute = signal('information');

  // --- Customer-level nav items ---
  readonly customerNavItems: NavItem[] = [
    { label: 'Information', route: 'information', icon: 'lucideInfo', disabled: false },
    { label: 'Manage Services', route: 'smt',     icon: 'lucideHeadset', disabled: false },
    { label: 'Contracts',   route: 'contracts',   icon: 'lucideFileText', disabled: false },
    { label: 'Invoice & Billing', route: 'invoice', icon: 'lucideReceipt', disabled: false },
    { label: 'Tariff Check',route: 'tariffcheck', icon: 'lucideCalculator', disabled: false },
  ];

  readonly uaNavItems = computed(() => {
    const ua = this.session.selectedUserAccount();
    if (!ua) return [];
    return [
      { label: 'User Account', route: `ua/${ua.id}/account-details`, icon: 'lucideUserRound', disabled: false },
    ] as NavItem[];
  });

  readonly psNavItems = computed(() => {
    const ps = this.session.selectedPhoneService();
    if (!ps) return [];
    return [
      { label: 'Phone Service',  route: `ps/${ps.id}/settings`,       icon: 'lucideSettings',       disabled: false },
      { label: 'Reachabilities', route: `ps/${ps.id}/reachabilities`, icon: 'lucidePhoneForwarded', disabled: false },
      { label: 'CC Numbers',     route: `ps/${ps.id}/numbers`,        icon: 'lucideHash',           disabled: false },
      { label: 'Notifications & Forwardings', route: `ps/${ps.id}/notifications`, icon: 'lucideBell',     disabled: false },
      { label: 'Contacts',       route: `ps/${ps.id}/contacts`,       icon: 'lucideContact',        disabled: false },
    ] as NavItem[];
  });

  readonly commNavItems = computed(() => {
    const ua = this.session.selectedUserAccount();
    if (!ua) return [];
    return [
      { label: 'Communication', route: `ua/${ua.id}/communication`, icon: 'lucideMail', disabled: false },
    ] as NavItem[];
  });

  // --- Display functions for entity selectors ---
  readonly uaToPsIdMap = computed(() => {
    const map = new Map<number, number>();
    for (const ps of this.session.phoneServices()) {
      if ((ps as any).userAccountId != null) {
        map.set((ps as any).userAccountId, ps.id);
      }
    }
    return map;
  });

  readonly uaDisplayFn = (ua: any) => {
    const name = [ua.lastName, ua.firstName].filter(Boolean).join(', ') || ua.fullName || `UA ${ua.id}`;
    const psId = this.uaToPsIdMap().get(ua.id);
    return `${name} ${ua.id} ${psId ?? ''}`;
  };

  readonly uaNameFn = (ua: any) => {
    return [ua.lastName, ua.firstName].filter(Boolean).join(', ') || ua.fullName || `UA ${ua.id}`;
  };

  readonly uaIdColumnFn = (ua: any) => String(ua.id);

  readonly psIdColumnFn = (ua: any) => {
    const psId = this.uaToPsIdMap().get(ua.id);
    return psId != null ? String(psId) : '\u2014';
  };

  readonly uaBreadcrumbName = computed(() => {
    const ua = this.session.selectedUserAccount();
    return ua ? this.uaNameFn(ua) : '';
  });

  readonly uaBreadcrumbIds = computed(() => {
    const ua = this.session.selectedUserAccount();
    if (!ua) return '';
    const ps = this.session.selectedPhoneService();
    const psId = ps ? ps.id : '\u2014';
    return `UA ${ua.id} · PS ${psId}`;
  });

  readonly statusFn = (entity: any) => entity.status ?? '';

  readonly isOnEntityRoute = computed(() => {
    const route = this.activeTabRoute();
    return ['account-details', 'reachabilities', 'communication', 'settings', 'numbers', 'notifications', 'contacts'].includes(route);
  });

  private readonly tabIconMap: Record<string, string> = {
    information:     'lucideInfo',
    smt:             'lucideHeadset',
    contracts:       'lucideFileText',
    invoice:         'lucideReceipt',
    tariffcheck:     'lucideCalculator',
    'account-details': 'lucideUserRound',
    communication:   'lucideMail',
    settings:        'lucideSettings',
    reachabilities:  'lucidePhoneForwarded',
    numbers:         'lucideHash',
    notifications:   'lucideBell',
    contacts:        'lucideContact',
  };

  readonly activeTabLabel = computed(() => {
    const route = this.activeTabRoute();
    const find = (arr: NavItem[]) =>
      arr.find(n => n.route === route || n.route.endsWith('/' + route))?.label;
    return (
      find(this.customerNavItems) ??
      find(this.uaNavItems()) ??
      find(this.psNavItems()) ??
      find(this.commNavItems()) ??
      ''
    );
  });

  readonly activeTabIcon = computed(() => this.tabIconMap[this.activeTabRoute()] ?? 'lucideInfo');

  // EB-3595: the SMT tab is a full-bleed external embed (drops the 1600px cap so
  // the iframe gets the full width beside the sidebar); other tabs keep the cap
  // for readability. Both are LEFT-anchored (no `mx-auto`) so the sidebar keeps a
  // fixed left position and never jumps when switching tabs.
  readonly isSmtRoute = computed(() => this.activeTabRoute() === 'smt');
  // Left padding is IDENTICAL on every tab (`pl-8`) so the sidebar is pinned and
  // never shifts; only SMT trims the RIGHT gutter + drops the cap for more width.
  private readonly shellWidth = computed(() => (this.isSmtRoute() ? 'max-w-none pl-8 pr-3' : 'max-w-[1600px] px-8'));
  readonly detailShellClass = computed(() => 'flex min-h-[calc(100vh-11rem)] gap-6 py-6 ' + this.shellWidth());
  readonly barShellClass = computed(() => 'flex h-14 items-center gap-3 text-sm ' + this.shellWidth());

  readonly statusBadgeVariant = computed(() => customerStatusTone(this.session.customer()?.status));

  // Breadcrumb badge is hidden via `@if (statusLabel())`, so keep the absent case
  // falsy (empty string) rather than the util's em-dash — otherwise a stray "—"
  // badge would show during load / when status is missing.
  readonly statusLabel = computed(() => {
    const status = this.session.customer()?.status;
    return status ? customerStatusLabel(status) : '';
  });

  readonly companyName = computed(() => this.session.customer()?.companyName ?? '');
  readonly brandName = computed(() => this.session.brandName());

  toggleSidebar(): void { this.sidebarExpanded.update(v => !v); }

  onUserAccountSelected(entity: { id: number }): void {
    this.session.selectUserAccountById(entity.id);
    this.router.navigate([`ua/${entity.id}/account-details`], { relativeTo: this.route });
  }

  onPhoneServiceSelected(entity: { id: number }): void {
    this.session.selectPhoneServiceById(entity.id);
    this.router.navigate([`ps/${entity.id}/settings`], { relativeTo: this.route });
  }

  openCallViewerForSelectedUa(): void {
    const ua = this.session.selectedUserAccount();
    if (!ua) return;
    const link = buildCallViewerLink('ua', { customerId: ua.customerId, userAccountId: ua.id });
    this.router.navigate(link.commands, { queryParams: link.queryParams });
  }

  constructor() {
    effect(() => {
      const isLoaded = this.session.isLoaded();
      if (!isLoaded || this.entitySyncDone) return;
      const urlSegments = this.router.url.split('/').filter(Boolean);
      if (urlSegments.length >= 4) {
        const entityType = urlSegments[1];
        const entityId = Number(urlSegments[2]);
        if (entityType === 'ua' && entityId) this.session.selectUserAccountById(entityId);
        else if (entityType === 'ps' && entityId) this.session.selectPhoneServiceById(entityId);
      }
      this.entitySyncDone = true;
    });
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const customerId = Number(params.get('customerId'));
      if (customerId && customerId !== this.session.customerId()) {
        // Clear stale data IMMEDIATELY so breadcrumb + entity selector
        // don't show the previous customer while new data loads
        this.session.clearSession();
        this.entitySyncDone = false;
        this.session.loadCustomer(customerId);
      }
    });
    this.updateActiveTab();
    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        this.updateActiveTab();
        // Re-sync entity selection from URL on every navigation (handles
        // same-customer UA/PS switches via search dialog or direct URL)
        this.syncEntityFromUrl();
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.session.clearSession();
  }

  /** Sync selected UA/PS from URL params — called on every NavigationEnd */
  private syncEntityFromUrl(): void {
    const urlSegments = this.router.url.split('/').filter(Boolean);
    if (urlSegments.length >= 4) {
      const entityType = urlSegments[1]; // 'ua' or 'ps'
      const entityId = Number(urlSegments[2]);
      if (entityType === 'ua' && entityId) {
        const current = this.session.selectedUserAccount();
        if (!current || current.id !== entityId) {
          this.session.selectUserAccountById(entityId);
        }
      } else if (entityType === 'ps' && entityId) {
        const current = this.session.selectedPhoneService();
        if (!current || current.id !== entityId) {
          this.session.selectPhoneServiceById(entityId);
        }
      }
    }
  }

  private updateActiveTab(): void {
    const urlSegments = this.router.url.split('/').filter(Boolean);
    if (urlSegments.length >= 4) {
      this.activeTabRoute.set(urlSegments[urlSegments.length - 1]);
    } else {
      const childRoute = this.route.firstChild?.snapshot.url[0]?.path;
      if (childRoute) this.activeTabRoute.set(childRoute);
    }
  }
}
